import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const API_BASE_URL = 'https://dolphin-app-33jp4.ondigitalocean.app';

function NotesBuddy() {
  const navigate = useNavigate();
  const [hasRefAccess, setHasRefAccess] = useState(false);

  /* ── Access-check logic ──────────────────────────────────────────────── */
  useEffect(() => {
    const checkRefAccess = () => {
      try {
        const userData = localStorage.getItem('userData');
        const user = userData ? JSON.parse(userData) : null;
        if (user && (user.role === 'super_admin' || user.email === 'info@easylearn.net.in')) {
          setHasRefAccess(true);
        } else {
          setHasRefAccess(localStorage.getItem('refAccess') === '1');
        }
      } catch { setHasRefAccess(false); }
    };
    checkRefAccess();
    const pollRefAccess = async () => {
      try {
        const userData = localStorage.getItem('userData');
        if (!userData) return;
        const user = JSON.parse(userData);
        if (!user?.email || user.role === 'super_admin' || user.email === 'info@easylearn.net.in') return;
        const res = await fetch(`${API_BASE_URL}/isfathena/user/can-access-reference?email=${encodeURIComponent(user.email)}`);
        const data = await res.json();
        const newVal = data.can_access ? '1' : '0';
        if (newVal !== localStorage.getItem('refAccess')) {
          localStorage.setItem('refAccess', newVal);
          setHasRefAccess(data.can_access);
        }
      } catch {}
    };
    const handleStorageEvent = (e) => {
      if (e.key === 'refAccessToggle') {
        try {
          const { email, value } = JSON.parse(e.newValue);
          const userData = localStorage.getItem('userData');
          if (userData) {
            const user = JSON.parse(userData);
            if (user.email === email) { localStorage.setItem('refAccess', value); setHasRefAccess(value === '1'); }
          }
        } catch {}
      }
      checkRefAccess();
    };
    const pollInterval = setInterval(pollRefAccess, 10000);
    window.addEventListener('storage', handleStorageEvent);
    window.addEventListener('refAccessChange', checkRefAccess);
    return () => { clearInterval(pollInterval); window.removeEventListener('storage', handleStorageEvent); window.removeEventListener('refAccessChange', checkRefAccess); };
  }, []);

  /* ── Copy / inspect protection ───────────────────────────────────────── */
  useEffect(() => {
    const handleContextMenu = (e) => { e.preventDefault(); return false; };
    const handleKeyDown = (e) => {
      if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J')) || (e.ctrlKey && e.key === 'U')) { e.preventDefault(); return false; }
    };
    const handleCopy = (e) => { e.preventDefault(); return false; };
    const handleDragStart = (e) => { e.preventDefault(); return false; };
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('dragstart', handleDragStart);
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.msUserSelect = 'none';
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('dragstart', handleDragStart);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.msUserSelect = '';
    };
  }, []);

  return (
    <div className="bg-black text-white min-h-screen overflow-x-hidden">
      <Navbar />

      <style>{`
        img { pointer-events:none; user-select:none; -webkit-user-drag:none; }
        * { box-sizing: border-box; }
      `}</style>

      {/* ── Starfield ─────────────────────────────────────────────────────── */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {[...Array(80)].map((_, i) => (
          <div key={i} className="absolute bg-white rounded-full opacity-30"
            style={{
              width: Math.random() * 2 + 1 + 'px',
              height: Math.random() * 2 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
            }}
          />
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          HERO
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-[55vh] sm:min-h-[65vh] lg:min-h-[70vh] flex items-center overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="/Assests/home_page_79.png"
            alt=""
            className="w-full h-full object-cover object-center"
          />
          {/* Strong left-side overlay so text is always readable */}
          <div className="absolute inset-0"
            style={{
              background: 'linear-gradient(to right, rgba(0,0,0,0.96) 0%, rgba(0,0,0,0.88) 40%, rgba(0,0,0,0.55) 65%, rgba(0,0,0,0.10) 100%)'
            }}
          />
        </div>

        {/* Hero content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-10 lg:px-24 py-14 sm:py-20 lg:py-28">
          <div style={{ maxWidth: '600px' }}>

            {/* Main heading — nowrap on desktop, wraps gracefully on mobile */}
            <h1
              className="font-extrabold text-white mb-3 leading-tight"
              style={{
                fontSize: 'clamp(1.4rem, 3.5vw, 2.6rem)',
                textShadow: '0 2px 12px rgba(0,0,0,0.8)',
                whiteSpace: 'normal',
              }}
            >
              NotesBuddy Notes Management Software
            </h1>

            {/* Subtitle */}
            <h2
              className="font-bold mb-5"
              style={{
                fontSize: 'clamp(0.95rem, 1.8vw, 1.2rem)',
                color: '#f97316',
                textShadow: '0 1px 6px rgba(0,0,0,0.7)',
              }}
            >
              Simplify Your Learning Journey
            </h2>

            {/* Description */}
            <p
              className="text-gray-200 leading-relaxed mb-7"
              style={{
                fontSize: 'clamp(0.82rem, 1.4vw, 1rem)',
                textShadow: '0 1px 4px rgba(0,0,0,0.8)',
                maxWidth: '420px',
              }}
            >
              Your all-in-one platform to organize, access, and master your study notes effortlessly.
              Built for students, educators, and lifelong learners.
            </p>

            {/* Buttons */}
            <div className="flex flex-wrap gap-3">
              <button
                className="text-white rounded-lg font-bold transition-all duration-200 hover:scale-105 shadow-xl hover:opacity-90"
                style={{
                  backgroundColor: '#c2410c',
                  padding: 'clamp(8px,1.2vw,12px) clamp(18px,2.5vw,28px)',
                  fontSize: 'clamp(0.8rem, 1.3vw, 0.95rem)',
                }}
              >
                Get Started Free
              </button>
              <button
                className="text-white rounded-lg font-bold transition-all duration-200 border hover:opacity-90"
                style={{
                  backgroundColor: '#9a3412',
                  borderColor: 'rgba(255,255,255,0.35)',
                  padding: 'clamp(8px,1.2vw,12px) clamp(18px,2.5vw,28px)',
                  fontSize: 'clamp(0.8rem, 1.3vw, 0.95rem)',
                }}
              >
                Explore Features
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          CHAPTER 1 — CHALLENGE
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative bg-black py-12 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-4">
            The Challenge of Managing Notes in a Digital World
          </h2>
          <p className="text-gray-400 text-sm sm:text-base lg:text-lg leading-relaxed max-w-3xl">
            In an era of information overload, keeping your notes organized shouldn't be the hardest
            part of learning. Let's explore the problem NotesBuddy was built to solve.
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          COMPLEXITY OF MODERN LEARNING
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="bg-black py-12 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-24 border-t border-gray-900">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-3 space-y-4">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-snug">
              The Complexity of Modern Learning
            </h2>
            <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
              Students and professionals are increasingly overwhelmed by scattered notes spread
              across apps, notebooks, and cloud drives. Finding the right information at the
              right time has become a daily struggle.
            </p>
            <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
              Traditional note-taking methods — pen and paper, basic text files — simply fail to
              keep pace with the demands of digital education and fast-moving workplaces.
            </p>
          </div>
          <div className="lg:col-span-2 rounded-2xl overflow-hidden shadow-2xl">
            <img src="/Assests/home_page_129.png" alt="Complexity of Modern Learning" className="w-full h-48 sm:h-60 lg:h-72 object-cover" />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          WHY NOTESBUDDY
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="bg-black py-12 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-24 border-t border-gray-900">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          <div className="rounded-2xl overflow-hidden shadow-2xl order-2 lg:order-1">
            <img src="/Assests/home_page_47.png" alt="Why NotesBuddy" className="w-full h-52 sm:h-72 lg:h-96 object-cover" />
          </div>
          <div className="space-y-5 order-1 lg:order-2">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-snug">
              Why NotesBuddy? The Need for a Smarter Solution
            </h2>
            <div className="space-y-3">
              <div className="border-l-4 pl-4 py-3 bg-gray-900/50 rounded-r-xl" style={{ borderColor: '#c2410c' }}>
                <h3 className="text-base font-bold mb-1" style={{ color: '#c2410c' }}>Bridge the Gap</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  NotesBuddy connects the act of note-taking with effective learning management, so nothing falls through the cracks.
                </p>
              </div>
              <div className="border-l-4 pl-4 py-3 bg-gray-900/50 rounded-r-xl" style={{ borderColor: '#c2410c' }}>
                <h3 className="text-base font-bold mb-1" style={{ color: '#c2410c' }}>Streamline Workflows</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Designed to organize, simplify, and enhance your entire study workflow — from capture to review.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          CHAPTER 2 BANNER
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative py-20 sm:py-28 lg:py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/Assests/home_page_60.png" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/65" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-24">
          <div className="bg-black/75 border border-gray-700 rounded-2xl p-6 sm:p-10 max-w-lg w-full">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-3 leading-snug">
              Introducing NotesBuddy — Learning Made Easy
            </h2>
            <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
              Discover a platform that combines simplicity on the surface with powerful capabilities under the hood. Welcome to smarter learning.
            </p>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          PLATFORM BUILT FOR SIMPLICITY
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="bg-black py-12 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-8">
            A Platform Built for Simplicity and Power
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-stretch">
            <div className="rounded-2xl p-8 sm:p-10 space-y-4 flex flex-col justify-center" style={{ backgroundColor: '#c2410c' }}>
              <h3 className="text-xl sm:text-2xl font-bold text-white">Just 3 Buttons</h3>
              <p className="text-white/90 text-sm sm:text-base leading-relaxed">
                Workshop participants interact with only three main buttons — keeping the experience frictionless and intuitive.
              </p>
              <p className="text-white/75 text-sm leading-relaxed">
                Behind the scenes, a sophisticated backend handles all the complexity so users never have to.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl min-h-48">
              <img src="/Assests/home_page_60.png" alt="Platform Interface" className="w-full h-full object-cover min-h-48 sm:min-h-64" />
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          USER-CENTRIC DESIGN
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="bg-black py-12 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-24 border-t border-gray-900">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-8">
            User-Centric Design by Tapan Das and ISF Edutech
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                title: 'Customized Experience',
                desc: 'Every element is tailored for a glitch-free, seamless user experience that feels polished and professional.',
                svg: <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" style={{ color: '#c2410c' }}><circle cx="12" cy="12" r="3"/><circle cx="12" cy="12" r="9"/><circle cx="4.5" cy="4.5" r="1.5"/><circle cx="19.5" cy="4.5" r="1.5"/><circle cx="4.5" cy="19.5" r="1.5"/><circle cx="19.5" cy="19.5" r="1.5"/></svg>,
              },
              {
                title: 'All Skill Levels',
                desc: "Whether you're a tech-savvy educator or a first-time digital learner, NotesBuddy meets you where you are.",
                svg: <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" style={{ color: '#c2410c' }}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"/></svg>,
              },
              {
                title: 'Reliable & Tested',
                desc: 'Built on proven infrastructure with rigorous quality assurance to ensure dependable performance.',
                svg: <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" style={{ color: '#c2410c' }}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"/></svg>,
              },
            ].map((item) => (
              <div key={item.title} className="bg-gray-900 rounded-xl border border-gray-800 hover:border-orange-600 transition-all duration-200 p-6 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="shrink-0">{item.svg}</div>
                  <h3 className="text-sm font-bold" style={{ color: '#c2410c' }}>{item.title}</h3>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          CHAPTER 3 — CORE FEATURES
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="bg-black py-12 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-24 border-t border-gray-900">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-4">
            Core Features That Transform Note Management
          </h2>
          <p className="text-gray-400 text-sm sm:text-base lg:text-lg leading-relaxed max-w-3xl">
            From intuitive organization to real-time collaboration, explore the features that
            make NotesBuddy indispensable for modern learners.
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          INTUITIVE NOTE ORGANIZATION
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-24" style={{ backgroundColor: '#9a3412' }}>
        <div className="max-w-7xl mx-auto">
          <div className="rounded-2xl p-6 sm:p-10 lg:p-14" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-8">
              Intuitive Note Organization and Access
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-10">
              {[
                { img: '/Assests/home_page_131.png', title: 'Centralized Dashboard', desc: 'Quickly retrieve and manage all your notes from a single, beautifully organized hub. No more hunting across apps.' },
                { img: '/Assests/home_page_132.png', title: 'Multimedia Support',    desc: 'Enrich your notes with text, images, and links — creating comprehensive, multi-layered study materials.' },
              ].map((item) => (
                <div key={item.title} className="space-y-3">
                  <div className="rounded-xl overflow-hidden shadow-xl">
                    <img src={item.img} alt={item.title} className="w-full h-40 sm:h-52 object-cover" />
                  </div>
                  <h3 className="text-base font-bold" style={{ color: '#fcd9c0' }}>{item.title}</h3>
                  <p className="text-white/80 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SEAMLESS THIRD-PARTY INTEGRATIONS
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative py-16 sm:py-24 lg:py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/Assests/home_page_133.png" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-24 flex justify-center">
          <div className="bg-black/85 border border-gray-700 rounded-2xl p-6 sm:p-10 lg:p-14 w-full max-w-4xl">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-8">
              Seamless Third-Party Integrations
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                { num: '1', title: 'Connect Effortlessly',    desc: 'NotesBuddy integrates with popular educational tools and platforms you already use — no complicated setup required.' },
                { num: '2', title: 'Expand Beyond Classrooms', desc: 'Break traditional boundaries by syncing notes with LMS platforms, cloud storage, and communication tools.' },
                { num: '3', title: 'Stay in Sync',            desc: 'Changes reflect instantly across connected platforms, ensuring your notes are always up to date everywhere.' },
              ].map((item) => (
                <div key={item.num} className="border border-gray-600 rounded-xl p-5 space-y-3 hover:border-orange-600 transition-all duration-200">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: '#c2410c' }}>{item.num}</div>
                  <h3 className="font-bold text-white text-sm">{item.title}</h3>
                  <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          REAL-TIME COLLABORATION
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-24" style={{ backgroundColor: '#9a3412' }}>
        <div className="max-w-7xl mx-auto">
          <div className="rounded-2xl p-6 sm:p-10 lg:p-14" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-8">
              Real-Time Collaboration and Group Features
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center">
              <div className="rounded-xl overflow-hidden shadow-xl">
                <img src="/Assests/home_page_133.png" alt="Real-Time Collaboration" className="w-full h-48 sm:h-64 lg:h-72 object-cover" />
              </div>
              <div className="space-y-4">
                <h3 className="text-xl font-bold" style={{ color: '#fcd9c0' }}>Study Together, Achieve More</h3>
                <p className="text-white/85 text-sm sm:text-base leading-relaxed">
                  NotesBuddy facilitates group study sessions and shared note-taking in real time.
                  Multiple users can contribute, edit, and review simultaneously.
                </p>
                <p className="text-white/70 text-sm leading-relaxed">
                  Enhance peer learning and teamwork dynamics with built-in collaboration tools designed for modern education.
                </p>
                <div className="flex gap-3 flex-wrap pt-2">
                  <button className="px-5 py-2.5 bg-black text-white rounded-lg font-semibold text-sm border border-white/30 hover:border-white transition-all duration-200">
                    Live Editing
                  </button>
                  <button className="px-5 py-2.5 bg-black text-white rounded-lg font-semibold text-sm border border-white/30 hover:border-white transition-all duration-200">
                    Shared Notebooks
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          CHAPTER 4 BANNER
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative py-20 sm:py-28 lg:py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/Assests/home_page_134.png" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-24">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-4">
            Benefits That Empower Learners and Educators
          </h2>
          <p className="text-gray-300 text-sm sm:text-base lg:text-lg leading-relaxed max-w-3xl">
            Beyond features, NotesBuddy delivers tangible benefits — from round-the-clock reliability to actionable learning insights.
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          24/7 SUPPORT STATS
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-24 relative overflow-hidden" style={{ backgroundColor: '#c2410c' }}>
        <div className="relative z-10 max-w-7xl mx-auto">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-10">
            24/7 Support and Reliable Performance
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-8">
            {[
              { stat: '99.9%', label: 'Uptime',     desc: 'Robust server redundancy ensures minimal downtime' },
              { stat: '24/7',  label: 'Support',    desc: 'Around-the-clock assistance for uninterrupted learning' },
              { stat: '<2s',   label: 'Load Time',  desc: 'Fast page response for a seamless experience' },
            ].map((item) => (
              <div key={item.stat} className="space-y-2">
                <div className="text-4xl sm:text-5xl font-extrabold text-white">{item.stat}</div>
                <div className="text-base font-bold text-black">{item.label}</div>
                <p className="text-black/80 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-black/80 text-sm sm:text-base leading-relaxed max-w-xl">
            NotesBuddy's infrastructure is built for reliability. With redundant servers and a dedicated support team, your learning never stops — day or night.
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          COURSE STATISTICS
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="bg-black py-12 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-10">
            Comprehensive Course Statistics and Insights
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <div className="flex flex-wrap gap-4 mb-5">
                <span className="flex items-center gap-2 text-xs sm:text-sm text-gray-400">
                  <span className="w-3 h-3 rounded inline-block" style={{ backgroundColor: '#9a3412' }} /> Before NotesBuddy
                </span>
                <span className="flex items-center gap-2 text-xs sm:text-sm text-gray-400">
                  <span className="w-3 h-3 rounded inline-block" style={{ backgroundColor: '#c2410c' }} /> With NotesBuddy
                </span>
              </div>
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      { name: 'Engagement',   before: 45, after: 88 },
                      { name: 'Completion',   before: 52, after: 91 },
                      { name: 'Metric',       before: 38, after: 79 },
                      { name: 'Retention',    before: 41, after: 76 },
                      { name: 'Satisfaction', before: 61, after: 97 },
                    ]}
                    margin={{ top: 10, right: 10, left: 0, bottom: 45 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#2d2d2d" />
                    <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 10 }} interval={0} angle={-12} textAnchor="end" height={60} />
                    <YAxis domain={[0, 100]} tick={{ fill: '#9ca3af', fontSize: 10 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#111', border: '1px solid #c2410c', borderRadius: '8px', color: '#fff' }}
                      formatter={(v, n) => [`${v}%`, n === 'before' ? 'Before' : 'With NotesBuddy']}
                    />
                    <Bar dataKey="before" radius={[4, 4, 0, 0]}>{[0,1,2,3,4].map(i => <Cell key={i} fill="#9a3412" />)}</Bar>
                    <Bar dataKey="after"  radius={[4, 4, 0, 0]}>{[0,1,2,3,4].map(i => <Cell key={i} fill="#c2410c" />)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl sm:text-2xl font-bold text-white">Track What Matters</h3>
              <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                Get a visual overview of learning progress and engagement metrics at a glance.
                NotesBuddy helps users and educators track performance effectively — turning raw data into actionable insights.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          FLEXIBILITY FOR DIVERSE LEARNING
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-24 relative overflow-hidden" style={{ backgroundColor: '#c2410c' }}>
        <div className="relative z-10 max-w-7xl mx-auto">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-10">
            Flexibility for Diverse Learning Needs
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
            {[
              { img: '/Assests/home_page_116.png', title: 'All Skill Levels',      desc: 'Suitable for institutions with learners ranging from beginners to advanced users.' },
              { img: '/Assests/home_page_126.png', title: 'Live Workshops',        desc: 'Supports real-time interactive workshop sessions with built-in note-taking tools.' },
              { img: '/Assests/home_page_133.png', title: 'Pre-Recorded Training', desc: 'Equally powerful for on-demand, self-paced learning with recorded content.' },
            ].map((item) => (
              <div key={item.title} className="space-y-3">
                <div className="rounded-xl overflow-hidden shadow-xl">
                  <img src={item.img} alt={item.title} className="w-full h-40 sm:h-52 object-cover" />
                </div>
                <h3 className="text-base font-bold text-white">{item.title}</h3>
                <p className="text-white/80 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          CHAPTER 5 — REAL-WORLD IMPACT
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="bg-black py-12 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-24 border-t border-gray-900">
        <div className="max-w-7xl mx-auto space-y-4">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">
            Real-World Impact and User Experiences
          </h2>
          <p className="text-gray-400 text-sm sm:text-base lg:text-lg leading-relaxed max-w-3xl">
            Don't just take our word for it — hear from the educators and learners who rely on NotesBuddy every day.
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SUCCESS STORIES
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="bg-black py-10 sm:py-14 px-4 sm:px-8 lg:px-24 border-t border-gray-900">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-8">
            Success Stories from Workshops and Training Sessions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { role: 'Workshop Coordinator', quote: '"NotesBuddy powered our online workshops with minimal user effort. Participants only needed three clicks to get started — it was incredibly smooth."' },
              { role: 'Educator Feedback',    quote: '"The simple UI paired with a powerful backend made it the perfect tool. Both tech-savvy and non-tech users praised it equally."' },
            ].map((item) => (
              <div key={item.role} className="relative border-2 rounded-2xl p-6 pt-9" style={{ borderColor: '#c2410c' }}>
                <span
                  className="absolute -top-4 left-5 text-white text-xs font-bold px-4 py-1.5 rounded-full"
                  style={{ backgroundColor: '#c2410c' }}
                >
                  {item.role}
                </span>
                <p className="text-gray-300 text-sm leading-relaxed italic">{item.quote}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          TRUSTED BY ISF EDUTECH
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="bg-black py-10 sm:py-14 px-4 sm:px-8 lg:px-24 border-t border-gray-900">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-10">
            Trusted by ISF Edutech and Educational Institutions
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="rounded-2xl p-8 sm:p-10 space-y-4" style={{ backgroundColor: '#c2410c' }}>
              <h3 className="text-xl font-bold text-white">Platform Partner</h3>
              <p className="text-white/90 text-sm sm:text-base leading-relaxed">
                <strong>Tapan Das</strong> is credited for customization excellence, delivering a tailored experience that institutions trust.
              </p>
              <p className="text-white/70 text-sm leading-relaxed">
                A proven track record of reliability and user satisfaction spanning multiple years.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6 sm:gap-8">
              {[
                { pct: '95%', label: 'User Satisfaction' },
                { pct: '98%', label: 'Session Reliability' },
              ].map((s) => (
                <div key={s.pct} className="text-center space-y-3">
                  <div className="w-24 h-24 sm:w-32 sm:h-32 mx-auto rounded-full border-4 flex items-center justify-center bg-gray-900" style={{ borderColor: '#c2410c' }}>
                    <span className="text-2xl sm:text-3xl font-extrabold text-white">{s.pct}</span>
                  </div>
                  <p className="text-gray-300 text-sm font-medium">{s.label}</p>
                </div>
              ))}
              <div className="col-span-2 flex flex-col items-center space-y-3">
                <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 flex items-center justify-center bg-gray-900" style={{ borderColor: '#c2410c' }}>
                  <span className="text-2xl sm:text-3xl font-extrabold text-white">90%</span>
                </div>
                <p className="text-gray-300 text-sm font-medium">Return Users</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          CHAPTER 6 — FUTURE
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="bg-black py-12 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-24 border-t border-gray-900">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-4">
            Looking Ahead — The Future of NotesBuddy
          </h2>
          <p className="text-gray-400 text-sm sm:text-base lg:text-lg leading-relaxed max-w-3xl">
            Innovation never stops. Here's what's on the horizon as NotesBuddy continues to evolve with the needs of modern education.
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          CONTINUOUS INNOVATION
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-24 relative overflow-hidden" style={{ backgroundColor: '#9a3412' }}>
        <div className="relative z-10 max-w-7xl mx-auto">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-10">
            Continuous Innovation and Upcoming Features
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {[
              {
                title: 'Version 3.0',
                desc: 'Enhanced capabilities with AI-powered note suggestions and smarter search functionality.',
                svg: (
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1 1 .03 2.798-1.332 2.798H4.13c-1.36 0-2.332-1.798-1.332-2.798L4.2 15.3" />
                  </svg>
                ),
              },
              {
                title: 'Evolving Platform',
                desc: 'Continuous updates driven by user feedback and emerging educational trends.',
                svg: (
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                  </svg>
                ),
              },
              {
                title: 'Broader Reach',
                desc: 'Expanding integrations and multilingual support for global learners.',
                svg: (
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                  </svg>
                ),
              },
            ].map((item) => (
              <div
                key={item.title}
                className="rounded-xl p-6 space-y-3 border hover:border-white/60 transition-all duration-200"
                style={{ backgroundColor: 'rgba(0,0,0,0.25)', borderColor: 'rgba(255,255,255,0.2)' }}
              >
                <div className="flex items-center gap-3">
                  <div className="shrink-0">{item.svg}</div>
                  <h3 className="text-sm font-bold text-white">{item.title}</h3>
                </div>
                <p className="text-white/80 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <p className="text-white/90 text-sm sm:text-base mb-8 max-w-xl leading-relaxed">
            NotesBuddy is committed to being the smartest companion on every learner's journey — today and tomorrow.
          </p>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => navigate('/reffrence?productId=5')}
              className="px-6 py-3 bg-black text-white border-2 border-white rounded-xl font-bold text-sm hover:bg-white hover:text-black transition-all duration-200"
            >
              Start Using NotesBuddy
            </button>
            <button
              onClick={() => window.open('https://notesbuddy.co.in', '_blank')}
              className="px-6 py-3 border-2 border-white text-white rounded-xl font-bold text-sm transition-all duration-200 hover:bg-white"
              style={{ backgroundColor: '#7c2d12' }}
            >
              Visit notesbuddy.co.in
            </button>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          FINAL CTA — SIMPLIFY YOUR LEARNING
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative bg-black py-12 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-24 border-t border-gray-900">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-5">
            Simplify Your Learning Journey with NotesBuddy
          </h2>
          <p className="text-gray-400 text-sm sm:text-base mb-10 max-w-3xl leading-relaxed">
            Organize, access, and master your study notes effortlessly with a smarter digital platform. Visit{' '}
            <a
              href="https://notesbuddy.co.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold hover:underline"
              style={{ color: '#c2410c' }}
            >
              notesbuddy.co.in
            </a>{' '}
            to explore powerful note management tools.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
            {[
              { title: 'Smart Note Organization', desc: 'Keep all your notes structured, searchable, and easy to access anytime.' },
              { title: 'Multimedia Learning',     desc: 'Enrich your notes with text, images, and resources for deeper learning.' },
            ].map((card) => (
              <div
                key={card.title}
                className="bg-gradient-to-br from-gray-900 to-black border-2 border-white rounded-xl p-6 hover:border-orange-600 transition-all duration-200"
              >
                <h3 className="text-base sm:text-lg font-bold text-white mb-3">{card.title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-4">
            {hasRefAccess && (
              <button
                onClick={() => navigate('/reffrence?productId=4')}
                className="px-6 py-3 text-white text-sm font-semibold rounded-lg transition-all duration-200 flex items-center gap-2 hover:opacity-90"
                style={{ backgroundColor: '#c2410c' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Reference
              </button>
            )}
            <button
              onClick={() => navigate('/reffrence?productId=4')}
              className="px-6 py-3 text-white text-sm font-semibold rounded-lg transition-all duration-200 hover:opacity-90"
              style={{ backgroundColor: '#c2410c' }}
            >
              Browse Notes
            </button>
            <button
              onClick={() => navigate('/reffrence?productId=4')}
              className="px-6 py-3 border-2 border-white text-white text-sm font-semibold rounded-lg hover:bg-white hover:text-black transition-all duration-200"
            >
              Explore Features
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}

export default NotesBuddy;